import { useState } from 'react';

import { Box, Button, Flex, H1, Icon, List, ListItem, Text } from '@opendoor/bricks/core';
import { SystemStyleInterpolation } from '@opendoor/bricks/system/utils';
import { resetLinkStyles } from '@opendoor/bricks/theme/eero/components/sharedCta';
import { generatePath } from '@remix-run/react';

import { useAuthAuthenticated } from 'platform/auth-fe';
import { Modal } from 'platform/bricks/Modal';
import { GlobalHeader } from 'platform/components/layout/GlobalHeader';
import { Layout, LayoutContent, useLayoutState } from 'platform/components/layout/Layout';
import { LegacyExternalLink, Navigate } from 'platform/remix-run/react';

import { useQualificationContext } from 'shared/helpers/QualificationContext';
import {
  ACCOUNT_URL,
  DIRECT_MAIL_PREFERENCES_URL,
  getBuyerEmailFrequencyURL,
  getHubSellingRoute,
} from 'shared/helpers/routes';

interface Item {
  name: string;
  displayName: string;
  shortDisplayName?: string;
  href: string | ((humanToken: string) => string);
}

interface ItemGroup {
  name: string;
  displayName: string;
  items: Item[];
}

const MORE_CONTENT: (Item | ItemGroup)[] = [
  {
    name: 'account',
    displayName: 'Your Account',
    shortDisplayName: 'Account',
    href: ACCOUNT_URL,
  },
  {
    name: 'settings',
    displayName: 'Settings',
    items: [
      {
        name: 'email-preferences',
        displayName: 'Email preferences',
        href: getBuyerEmailFrequencyURL,
      },
      {
        name: 'direct-mail-preferences',
        displayName: 'Direct mail preferences',
        href: DIRECT_MAIL_PREFERENCES_URL,
      },
    ],
  },
];

/**
 * The More page allows users to access pages that don't fit in the main navigation.
 */
export const MobileMorePage: React.FC = () => {
  const { addressUUID } = useQualificationContext();

  // The More page doesn't exist on desktop, so reroute to overview page.
  const state = useLayoutState();
  if (state === 'desktop') {
    return <Navigate to={generatePath(getHubSellingRoute(), { addressUUID })} />;
  }

  return (
    <Layout data-testid="more" renderHeader={() => <GlobalHeader title="More" />}>
      <LayoutContent p={7} pt="90px" pb="60vh">
        <ItemList items={MORE_CONTENT} />
      </LayoutContent>
    </Layout>
  );
};

export const DesktopMoreContent = withItemGroupModal(({ setOpenedItemGroup }) => {
  const { user } = useAuthAuthenticated();
  const state = useLayoutState();

  const sharedLinkStyles: SystemStyleInterpolation = {
    ...resetLinkStyles,
    display: 'block',
    color: 'neutrals100',
    fontWeight: 'regular',
    textDecoration: 'none',
    mb: [null, null, 4, 7],
    fontSize: ['s0', null, null, 's1'],
  };

  return (
    <List variant="text">
      {MORE_CONTENT.map((item) => {
        if ('href' in item) {
          let href: string | undefined = undefined;
          if (typeof item.href === 'string') {
            href = item.href;
          } else {
            href = item.href(user.humanToken);
          }
          const displayName: string =
            state === 'tablet' && item.shortDisplayName ? item.shortDisplayName : item.displayName;
          return (
            <ListItem key={item.name}>
              <LegacyExternalLink
                variant="inline-secondary"
                sx={sharedLinkStyles}
                analyticsName={item.name}
                href={href}
                target="_blank"
                aria-label={`${displayName} (opens in a new window)`}
              >
                {displayName}
              </LegacyExternalLink>
            </ListItem>
          );
        }

        return (
          <ListItem key={item.name}>
            <Button
              variant="inline-secondary-link"
              sx={sharedLinkStyles}
              name={item.name}
              onClick={() => setOpenedItemGroup(item)}
              aria-label={item.displayName}
              analyticsName={`cake-layout-more-${item.name}`}
            >
              {item.displayName}
            </Button>
          </ListItem>
        );
      })}
    </List>
  );
});

interface ItemGroupModalProps {
  setOpenedItemGroup: (itemGroup: ItemGroup) => void;
}

/* HOC that allows any component to open item groups in a modal. */
function withItemGroupModal<P>(
  Component: React.ComponentType<P & ItemGroupModalProps>,
): React.ComponentType<P> {
  const NewComponent = (props: P) => {
    const [openedItemGroup, setOpenedItemGroup] = useState<ItemGroup>();
    return (
      <>
        <Component {...props} setOpenedItemGroup={setOpenedItemGroup} />
        <Modal
          analyticsName={
            openedItemGroup !== undefined ? `cake-more-${openedItemGroup.name}-modal` : ''
          }
          isOpen={openedItemGroup !== undefined}
          showClose
          onRequestClose={() => setOpenedItemGroup(undefined)}
          desktopAndTabletSize="lg"
          aria-labelledby="more-item-group-title"
        >
          {openedItemGroup && (
            <Box p={11} pt={0}>
              <H1 mb={9} id="more-item-group-title">
                {openedItemGroup.displayName}
              </H1>
              <ItemList items={openedItemGroup.items} />
            </Box>
          )}
        </Modal>
      </>
    );
  };
  NewComponent.displayName = `${Component.displayName}WithItemGroupModal`;
  return NewComponent;
}

const ItemList: React.FC<{ items: (Item | ItemGroup)[] }> = ({ items }) => {
  return (
    <Box width="100%">
      {items.map((item) => {
        return <ItemRow key={item.name} item={item} />;
      })}
    </Box>
  );
};

const ItemRow = withItemGroupModal<{ item: Item | ItemGroup }>(({ item, setOpenedItemGroup }) => {
  const { user } = useAuthAuthenticated();

  if ('href' in item) {
    let href: string | undefined = undefined;
    if (typeof item.href === 'string') {
      href = item.href;
    } else {
      href = item.href(user.humanToken);
    }
    return (
      <LegacyExternalLink
        variant="inline-secondary"
        analyticsName={item.name}
        href={href}
        target="_blank"
        aria-label={`${item.displayName} (opens in a new window)`}
        textDecoration="none"
      >
        <ItemRowContent item={item} />
      </LegacyExternalLink>
    );
  }

  return (
    <Button
      variant="ghost"
      name={item.name}
      onClick={() => setOpenedItemGroup(item)}
      display="block"
      padding="0"
      sx={{ borderRadius: 'none' }}
      aria-label={item.displayName}
      analyticsName={`cake-layout-more-mobile-${item.name}`}
      width="100%"
    >
      <ItemRowContent item={item} />
    </Button>
  );
});

const ItemRowContent = ({ item }: { item: Item | ItemGroup }) => (
  <Flex
    pl={[5, null, 0, 7]}
    pr={[5, null, 11, 7]}
    py={4}
    justifyContent="space-between"
    alignItems="center"
    cursor="pointer"
  >
    <Text size="s3" fontWeight="semibold">
      {item.displayName}
    </Text>
    <Icon name="chevron-right" size={16} />
  </Flex>
);
